"use client"

import { useEffect, useRef } from "react"
import { Pie } from "react-chartjs-2"
import { Chart as ChartJS, ArcElement, Tooltip, Legend, type ChartOptions, type ChartType } from "chart.js"
import { Box, Typography } from "@mui/material"

declare module 'chart.js' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface PluginOptionsByType<TType extends ChartType> {
    valueLabel?: {
      showDecimals?: boolean;
      mode?: "cantidad" | "porcentaje";
    };
  }
}

ChartJS.register(ArcElement, Tooltip, Legend)

interface PieChartProps {
  title: string
  data: {
    id: string
    label: string
    value: number
    color?: string
  }[]
  selectedIndex: number
  onSelect: (index: number) => void
  showDecimals?: boolean
}

const valueLabelPlugin = {
  id: 'pieValueLabel',
  afterDatasetsDraw(chart: any) {
    const { ctx, data } = chart
    const dataset = data.datasets[0]
    const values = dataset.data
    const meta = chart.getDatasetMeta(0)
    if (!meta || !meta.data) return

    const showDecimals = chart.options.plugins?.valueLabel?.showDecimals ?? true

    ctx.save()
    ctx.font = 'bold 12px sans-serif'
    ctx.fillStyle = '#000000'
    ctx.textBaseline = 'middle'
    ctx.textAlign = 'center'

    meta.data.forEach((element: any, index: number) => {
      const value = values[index]
      if (value === undefined || value === null) return

      const position = element.tooltipPosition()
      const x = position.x
      const y = position.y

      let formattedValue: string
      if (showDecimals) {
        formattedValue = value.toLocaleString('en-US', { maximumFractionDigits: 2 })
      } else {
        formattedValue = value.toLocaleString('en-US', { maximumFractionDigits: 0 })
      }

      ctx.fillText(formattedValue, x, y)
    })

    ctx.restore()
  }
}

export function PieChart({ title, data, selectedIndex, onSelect, showDecimals = true }: PieChartProps) {
  const chartRef = useRef<any>(null)

  const chartData = {
    labels: data.map((item) => item.label),
    datasets: [
      {
        data: data.map((item) => item.value),
        backgroundColor: data.map((item, index) =>
          index === selectedIndex
            ? item.color || "rgba(168, 85, 247, 0.8)"
            : item.color || "rgba(168, 85, 247, 0.4)"
        ),
        borderColor: data.map(() => "white"),
        borderWidth: 2,
      },
    ],
  }

  const options: ChartOptions<"pie"> = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (elements.length > 0) {
        onSelect(elements[0].index)
      }
    },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          font: { size: 11 },
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.parsed
            if (showDecimals) {
              return value.toLocaleString('en-US', { maximumFractionDigits: 2 })
            } else {
              return value.toLocaleString('en-US', { maximumFractionDigits: 0 })
            }
          },
        },
      },
      valueLabel: {
        showDecimals,
      },
    },
  }

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.update()
    }
  }, [data, selectedIndex, showDecimals])

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: "#2563eb", fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Pie ref={chartRef} data={chartData} options={options} plugins={[valueLabelPlugin]} />
      </Box>
    </Box>
  )
}